import { create } from 'zustand';

import type {
  DuplicateCheckResult,
  FeatureRequest,
  LinearLabel,
  LinearTeam,
  TranscriptSource,
  UpdateUploadRequest,
  UploadSummary,
} from '../types/featureRequests';
import {
  bulkCreateLinearIssues,
  checkDuplicates,
  createLinearIssue,
  deleteUpload,
  getLinearLabels,
  getLinearTeams,
  getUploadFeatures,
  getUploadTranscript,
  listUploads,
  processTranscript,
  updateUpload,
} from '../api/featureRequests';

interface FeatureRequestState {
  // Transcript input
  transcript: string;
  transcriptSource: TranscriptSource;
  callerOrgId: string | null;
  callerOrgName: string | null;
  callerUserId: string | null;
  callerUserName: string | null;
  callDate: string | null;

  // Processing state
  isProcessing: boolean;
  isLoadingFeatures: boolean;
  processingError: string | null;

  // Current upload/features
  currentUploadId: string | null;
  currentUpload: UploadSummary | null;
  currentTranscript: string | null;
  featureRequests: FeatureRequest[];
  extractionSummary: string;

  // Selection for Linear creation
  selectedFeatureIds: Set<string>;

  // Duplicate check
  duplicateResults: Map<string, DuplicateCheckResult>;
  checkingDuplicateId: string | null;

  // Linear options
  linearTeams: LinearTeam[];
  linearLabels: LinearLabel[];
  selectedTeamId: string | null;

  // Creation state
  isCreatingTickets: boolean;
  creationResults: { success: number; failed: number } | null;

  // History
  uploads: UploadSummary[];
  isLoadingHistory: boolean;

  // Actions
  setTranscript: (text: string) => void;
  setTranscriptSource: (source: TranscriptSource) => void;
  setCallerOrg: (orgId: string | null, orgName: string | null) => void;
  setCallerUser: (userId: string | null, userName: string | null) => void;
  setCallDate: (date: string | null) => void;
  processTranscriptAction: () => Promise<void>;
  loadUploadFeatures: (uploadId: string) => Promise<void>;
  loadUploadTranscript: (uploadId: string) => Promise<string | null>;
  loadUploads: () => Promise<void>;
  loadLinearTeams: () => Promise<void>;
  loadLinearLabels: (teamId?: string) => Promise<void>;
  toggleFeatureSelection: (id: string) => void;
  selectAllFeatures: () => void;
  clearSelection: () => void;
  setSelectedTeam: (teamId: string | null) => void;
  checkDuplicatesAction: (featureId: string) => Promise<DuplicateCheckResult | null>;
  createSingleTicket: (
    featureId: string,
    skipDuplicateCheck?: boolean
  ) => Promise<boolean>;
  createSelectedTickets: (skipDuplicateCheck?: boolean) => Promise<void>;
  updateFeatureLocally: (featureId: string, updates: Partial<FeatureRequest>) => void;
  updateUploadDetails: (updates: UpdateUploadRequest) => Promise<boolean>;
  deleteCurrentUpload: () => Promise<boolean>;
  reset: () => void;
  clearError: () => void;
}

export const useFeatureRequestStore = create<FeatureRequestState>((set, get) => ({
  // Initial state
  transcript: '',
  transcriptSource: 'customer_call',
  callerOrgId: null,
  callerOrgName: null,
  callerUserId: null,
  callerUserName: null,
  callDate: null,
  isProcessing: false,
  isLoadingFeatures: false,
  processingError: null,
  currentUploadId: null,
  currentUpload: null,
  currentTranscript: null,
  featureRequests: [],
  extractionSummary: '',
  selectedFeatureIds: new Set(),
  duplicateResults: new Map(),
  checkingDuplicateId: null,
  linearTeams: [],
  linearLabels: [],
  selectedTeamId: null,
  isCreatingTickets: false,
  creationResults: null,
  uploads: [],
  isLoadingHistory: false,

  // Actions
  setTranscript: (text) => set({ transcript: text }),

  setTranscriptSource: (source) => set({ transcriptSource: source }),

  setCallerOrg: (orgId, orgName) =>
    set({ callerOrgId: orgId, callerOrgName: orgName }),

  setCallerUser: (userId, userName) =>
    set({ callerUserId: userId, callerUserName: userName }),

  setCallDate: (date) => set({ callDate: date }),

  processTranscriptAction: async () => {
    const {
      transcript,
      transcriptSource,
      callerOrgId,
      callerOrgName,
      callerUserId,
      callerUserName,
      callDate,
    } = get();

    if (!transcript.trim() || transcript.length < 50) {
      set({ processingError: 'Please enter at least 50 characters of transcript text' });
      return;
    }

    set({
      isProcessing: true,
      processingError: null,
      featureRequests: [],
      extractionSummary: '',
      selectedFeatureIds: new Set(),
      creationResults: null,
    });

    try {
      const response = await processTranscript({
        transcript,
        source: transcriptSource,
        callerOrgId: callerOrgId || undefined,
        callerOrgName: callerOrgName || undefined,
        callerUserId: callerUserId || undefined,
        callerUserName: callerUserName || undefined,
        callDate: callDate || undefined,
      });

      if (response.status === 'failed') {
        set({
          isProcessing: false,
          processingError: response.errorMessage || 'Failed to process transcript',
        });
        return;
      }

      set({
        isProcessing: false,
        currentUploadId: response.uploadId,
        featureRequests: response.featureRequests,
        extractionSummary: response.summary,
      });

      // Reload uploads list
      get().loadUploads();
    } catch (error) {
      set({
        isProcessing: false,
        processingError:
          error instanceof Error ? error.message : 'Failed to process transcript',
      });
    }
  },

  loadUploadFeatures: async (uploadId) => {
    set({ isLoadingFeatures: true, processingError: null });

    try {
      const features = await getUploadFeatures(uploadId);
      // Find the upload from the list to get its details
      const upload = get().uploads.find((u) => u.id === uploadId) || null;
      set({
        isLoadingFeatures: false,
        currentUploadId: uploadId,
        currentUpload: upload,
        currentTranscript: null,
        featureRequests: features,
        selectedFeatureIds: new Set(),
        creationResults: null,
      });
    } catch (error) {
      set({
        isLoadingFeatures: false,
        processingError:
          error instanceof Error ? error.message : 'Failed to load features',
      });
    }
  },

  loadUploadTranscript: async (uploadId) => {
    try {
      const transcript = await getUploadTranscript(uploadId);
      set({ currentTranscript: transcript });
      return transcript;
    } catch (error) {
      console.error('Failed to load transcript:', error);
      return null;
    }
  },

  loadUploads: async () => {
    set({ isLoadingHistory: true });

    try {
      const response = await listUploads(50);
      const { currentUploadId } = get();
      // If we have a current upload ID, find and set the current upload from the list
      const currentUpload = currentUploadId
        ? response.uploads.find((u) => u.id === currentUploadId) || null
        : null;
      set({
        uploads: response.uploads,
        currentUpload,
        isLoadingHistory: false,
      });
    } catch (error) {
      console.error('Failed to load uploads:', error);
      set({ isLoadingHistory: false });
    }
  },

  loadLinearTeams: async () => {
    try {
      const teams = await getLinearTeams();
      set({ linearTeams: teams });

      // Set default team if not already set - prefer PEL team
      if (teams.length > 0 && !get().selectedTeamId) {
        const pelTeam = teams.find((t) => t.key === 'PEL');
        set({ selectedTeamId: pelTeam?.id || teams[0].id });
      }
    } catch (error) {
      console.error('Failed to load Linear teams:', error);
    }
  },

  loadLinearLabels: async (teamId) => {
    try {
      const labels = await getLinearLabels(teamId);
      set({ linearLabels: labels });
    } catch (error) {
      console.error('Failed to load Linear labels:', error);
    }
  },

  toggleFeatureSelection: (id) =>
    set((state) => {
      const newSet = new Set(state.selectedFeatureIds);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return { selectedFeatureIds: newSet };
    }),

  selectAllFeatures: () =>
    set((state) => ({
      selectedFeatureIds: new Set(
        state.featureRequests.filter((f) => !f.linearIssueId).map((f) => f.id)
      ),
    })),

  clearSelection: () => set({ selectedFeatureIds: new Set() }),

  setSelectedTeam: (teamId) => set({ selectedTeamId: teamId }),

  checkDuplicatesAction: async (featureId) => {
    set({ checkingDuplicateId: featureId });

    try {
      const result = await checkDuplicates(featureId);
      set((state) => ({
        checkingDuplicateId: null,
        duplicateResults: new Map(state.duplicateResults).set(featureId, result),
      }));
      return result;
    } catch (error) {
      console.error('Failed to check duplicates:', error);
      set({ checkingDuplicateId: null });
      return null;
    }
  },

  createSingleTicket: async (featureId, skipDuplicateCheck = false) => {
    const { selectedTeamId } = get();

    set({ isCreatingTickets: true });

    try {
      const result = await createLinearIssue({
        featureRequestId: featureId,
        teamId: selectedTeamId || undefined,
        skipDuplicateCheck,
      });

      if (result.success) {
        // Update the feature with Linear info
        set((state) => ({
          featureRequests: state.featureRequests.map((f) =>
            f.id === featureId
              ? {
                  ...f,
                  linearIssueId: result.linearIssueId,
                  linearIssueUrl: result.linearIssueUrl,
                  linearIssueIdentifier: result.linearIssueIdentifier,
                }
              : f
          ),
          selectedFeatureIds: new Set(
            [...state.selectedFeatureIds].filter((id) => id !== featureId)
          ),
          isCreatingTickets: false,
        }));
        return true;
      } else {
        set({
          isCreatingTickets: false,
          processingError: result.error || 'Failed to create ticket',
        });
        return false;
      }
    } catch (error) {
      set({
        isCreatingTickets: false,
        processingError:
          error instanceof Error ? error.message : 'Failed to create ticket',
      });
      return false;
    }
  },

  createSelectedTickets: async (skipDuplicateCheck = false) => {
    const { selectedFeatureIds, selectedTeamId } = get();

    if (selectedFeatureIds.size === 0) return;

    set({ isCreatingTickets: true, creationResults: null });

    try {
      const result = await bulkCreateLinearIssues(
        [...selectedFeatureIds],
        selectedTeamId || undefined,
        undefined,
        skipDuplicateCheck
      );

      // Update features with Linear info
      set((state) => {
        const updatedFeatures = state.featureRequests.map((f) => {
          const created = result.created.find((c) => c.featureRequestId === f.id);
          if (created && created.success) {
            return {
              ...f,
              linearIssueId: created.linearIssueId,
              linearIssueUrl: created.linearIssueUrl,
              linearIssueIdentifier: created.linearIssueIdentifier,
            };
          }
          return f;
        });

        return {
          featureRequests: updatedFeatures,
          selectedFeatureIds: new Set(),
          isCreatingTickets: false,
          creationResults: {
            success: result.totalCreated,
            failed: result.totalFailed,
          },
        };
      });
    } catch (error) {
      set({
        isCreatingTickets: false,
        processingError:
          error instanceof Error ? error.message : 'Failed to create tickets',
      });
    }
  },

  updateFeatureLocally: (featureId, updates) =>
    set((state) => ({
      featureRequests: state.featureRequests.map((f) =>
        f.id === featureId ? { ...f, ...updates } : f
      ),
    })),

  updateUploadDetails: async (updates) => {
    const { currentUploadId } = get();
    if (!currentUploadId) return false;

    try {
      const updatedUpload = await updateUpload(currentUploadId, updates);
      set((state) => ({
        currentUpload: updatedUpload,
        // Also update in the uploads list
        uploads: state.uploads.map((u) =>
          u.id === currentUploadId ? updatedUpload : u
        ),
      }));
      return true;
    } catch (error) {
      console.error('Failed to update upload:', error);
      set({
        processingError:
          error instanceof Error ? error.message : 'Failed to update meeting details',
      });
      return false;
    }
  },

  deleteCurrentUpload: async () => {
    const { currentUploadId } = get();
    if (!currentUploadId) return false;

    try {
      await deleteUpload(currentUploadId);
      set((state) => ({
        currentUploadId: null,
        currentUpload: null,
        currentTranscript: null,
        featureRequests: [],
        extractionSummary: '',
        selectedFeatureIds: new Set(),
        duplicateResults: new Map(),
        creationResults: null,
        uploads: state.uploads.filter((u) => u.id !== currentUploadId),
      }));
      return true;
    } catch (error) {
      console.error('Failed to delete upload:', error);
      set({
        processingError:
          error instanceof Error ? error.message : 'Failed to delete transcript',
      });
      return false;
    }
  },

  reset: () =>
    set({
      transcript: '',
      callerOrgId: null,
      callerOrgName: null,
      callerUserId: null,
      callerUserName: null,
      callDate: null,
      isProcessing: false,
      isLoadingFeatures: false,
      processingError: null,
      currentUploadId: null,
      currentUpload: null,
      currentTranscript: null,
      featureRequests: [],
      extractionSummary: '',
      selectedFeatureIds: new Set(),
      duplicateResults: new Map(),
      creationResults: null,
    }),

  clearError: () => set({ processingError: null }),
}));
